// components/ArtworkNotes/components/NoteList.tsx
import React, { useState, useMemo } from 'react';
import { Note } from '../../models/Note';
import NoteCard from './NoteCard';

import NoteSearch from './NoteSearch';
import { useDebounce } from '../../hooks/useDebounce';
import { useUser } from '../../components/UserContext';
import { useReferenceData } from '../../components/ReferenceDataContext';
interface NoteListProps {
    notes: Note[];
    loading: boolean;
    onEdit: (note: Note) => void;
    onDelete: (noteId: number) => void;
    onSearch: (search: string) => void;
}

type SortOption = 'updated' | 'title' | 'type';

const NoteList: React.FC<NoteListProps> = ({ notes, loading, onEdit, onDelete, onSearch }) => {

    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;

    const { noteTypes, noteTypesState } = useReferenceData();
    const [sortBy, setSortBy] = useState<SortOption>('updated');

    //Search
    const [searchInput, setSearchInput] = useState<string>('');
    const debouncedSearch = useDebounce(searchInput, 600);
    const handleSearchChange = (search: string): void => {
        setSearchInput(search);
        onSearch(search);
    };

    const noteTypeMap = useMemo(() => {
        const map = new Map<number, string>();
        noteTypes?.forEach(nt => map.set(nt.noteTypeId, nt.name));
        return map;
    }, [noteTypes]);

    const sortedNotes = useMemo(() => {
        return [...notes].sort((a, b) => {
            switch (sortBy) {
                case "title":
                    return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

                case "type": {
                    const nameA = noteTypeMap.get(a.noteTypeId) ?? "";
                    const nameB = noteTypeMap.get(b.noteTypeId) ?? "";
                    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
                }

                case "updated":
                default:
                    return (
                        new Date(b.updatedAt).getTime() -
                        new Date(a.updatedAt).getTime()
                    );
            }
        });
    }, [notes, sortBy, noteTypeMap]);


    if (loading) {
        return <div className="loading">Loading notes...</div>;
    }

    return (
        <div className="note-list">
            <div className="list-header">
                <h4>Notes</h4>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="sort-select"
                    disabled={loading}
                >
                    <option value="updated">Last Updated</option>
                    <option value="title">Title</option>
                    <option value="type">Type</option>
                </select>
            </div>
            <div className="list-header">
                <NoteSearch
                    value={searchInput}
                    onChange={handleSearchChange}
                    disabled={loading}
                />
            </div>
            <div className="notes-grid">
                {sortedNotes.map(note => (
                    <NoteCard
                        key={note.id}
                        note={note}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        disabled={!canEditArtwork}
                    />
                ))}
            </div>

            {sortedNotes.length === 0 && (
                <div className="empty-state">
                    <p>No notes found</p>
                </div>
            )}
        </div>
    );
};

export default NoteList;